import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { networkQueryKeys } from '#api';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { hostsCountQueryOptions, makeMaxHostsQueryOptions } from '#api/hosts.queries.ts';
import { makeNetworkQueryOptions } from '#api/networks.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { getIPv6Support } from '#util/ipv6Support.ts';
import type { useUpdateNetworkMutation } from './useUpdateNetworkMutation.ts';

type Props = {
  networkId: string;
  updateNetwork: ReturnType<typeof useUpdateNetworkMutation>;
  onCancel: () => void;
  onUpdate: () => void;
};

const FORM_ID = 'update-network-form';

export function UpdateNetworkModalBody({ networkId, updateNetwork, onCancel, onUpdate }: Props) {
  const queryClient = useQueryClient();
  // Every host below an IPv6 floor also has an update available, so that filter bounds the fetch to the hosts worth checking.
  const outdatedCandidatesFilter = { networkID: networkId, updateAvailable: true };
  const [
    {
      data: { data: network },
    },
    {
      data: { data: outdatedCandidates },
    },
    { data: outdatedCandidateCount },
  ] = useSuspenseQueries({
    queries: [
      makeNetworkQueryOptions(networkId),
      makeMaxHostsQueryOptions(outdatedCandidatesFilter),
      hostsCountQueryOptions(outdatedCandidatesFilter),
    ],
  });
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ defaultValues: { confirmation: '' } });

  if (network.certVersion !== 1) {
    return (
      <>
        <ModalBody>
          <p className="p">
            <Code className="wrap-anywhere">{network.name}</Code> has already been updated. Every host on it gets an
            IPv6 address the next time it checks in.
          </p>
        </ModalBody>
        <ModalFooter>
          <ConfirmButtonGroup
            variant="right"
            primaryButton={
              <Button variant="primary" onClick={onCancel}>
                Done
              </Button>
            }
          />
        </ModalFooter>
      </>
    );
  }

  const outdatedCount = outdatedCandidates.filter((host) => getIPv6Support(host) === 'outdated').length;
  // The max-hosts list stops after a fixed number of pages, so on a very large network some hosts go unchecked.
  const isTruncated = outdatedCandidates.length < outdatedCandidateCount;
  // The hosts page can't filter by client version; hosts with an update available are the superset it can show.
  const networkHostsTo = `/hosts?filter.networkID=${networkId}&filter.updateAvailable=true`;

  const onSubmit = handleSubmit(async () => {
    try {
      await updateNetwork.mutateAsync(networkId);
      onUpdate();
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ message }) => {
          setError('root.global', { message });
          return true;
        });
        // The usual cause is an update from another tab; refetching swaps in the already-updated state.
        void queryClient.invalidateQueries({ queryKey: networkQueryKeys.all() });
      } else {
        setError('root.global', { message: 'An unexpected error occurred' });
        trackError(error);
      }
    }
  });

  return (
    <>
      <ModalBody>
        <p className="p">
          Updating <Code className="wrap-anywhere">{network.name}</Code> to dual-stack assigns every host in the network
          an IPv6 address alongside its current IPv4 address. Each host picks up its new address the next time it checks
          in. You can enable{' '}
          <Link
            className="a"
            to="https://www.defined.net/docs/get-started/managed-lighthouses/"
            target="_blank"
            rel="help"
          >
            managed lighthouses
          </Link>{' '}
          afterward.
        </p>
        {outdatedCount > 0 || isTruncated ?
          <Banner variant="alert" className="mb-16">
            {outdatedCount > 0 ?
              <>
                {isTruncated ? 'At least ' : ''}
                {formatHostCount(outdatedCount)} {outdatedCount === 1 ? 'runs' : 'run'} a client too old for IPv6 and
                may lose connectivity after the network update.
              </>
            : <>
                Couldn’t check all {formatHostCount(outdatedCandidateCount)} with updates available, so some may run a
                client too old for IPv6 and lose connectivity after the network update.
              </>
            }{' '}
            Update to DNClient 0.9.3 or Mobile Nebula 0.10.0 first. <Link to={networkHostsTo}>View hosts</Link>
          </Banner>
        : null}
        <form id={FORM_ID} onSubmit={onSubmit}>
          <p className="p">
            <strong>This can’t be undone.</strong>
          </p>
          <Field label={`To confirm, type “${network.name}” below`}>
            <Input
              className="width-100"
              autoComplete="off"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- typing the name is the only thing to do in this dialog
              autoFocus={true}
              {...register('confirmation', {
                validate: (value) => value === network.name || 'Please type the network name exactly',
              })}
            />
            {errors.confirmation?.message ?
              <ErrorMessage>{errors.confirmation.message}</ErrorMessage>
            : null}
          </Field>
          {errors.root?.global?.message ?
            <ErrorMessage>{errors.root.global.message}</ErrorMessage>
          : null}
        </form>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form={FORM_ID} variant="primary" disabled={updateNetwork.isPending}>
              {updateNetwork.isPending ? 'Updating…' : 'Update network'}
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </>
  );
}

function formatHostCount(count: number) {
  return `${count.toLocaleString()} ${count === 1 ? 'host' : 'hosts'}`;
}
